import { useMutation } from '@apollo/client';
import { useEffect } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { UPDATE_PIECE } from '../../../graphql/campaign/mutation';
import { useNotification } from '../../../hooks/useNotification';
import { usePrompt } from '../../../hooks/usePrompt';
import { getActionMode } from '../../../utils/actionMode';
import { Button } from '../../common/Button';
import { DatePickerInput } from '../../inputs/DatePicker';
import { ActionTypeCom } from '../content/ActionType';

export function ActionDate({
  piece,
  total,
  refetch,
  refectDates,
  indexAction,
}: {
  piece: any;
  total: number;
  refetch: any;
  refectDates: any;
  indexAction: number;
}) {
  const { t } = useTranslation('common');
  const actionMode = getActionMode(piece.platform, piece.type);
  const isInterview = actionMode === 'interview';
  const dateLabel =
    actionMode === 'event'
      ? t('singleCampaign.eventDate')
      : t('singleCampaign.publicationDate');
  const {
    control,
    setValue,
    handleSubmit,
    reset,
    formState: { dirtyFields },
  } = useForm({
    defaultValues: {
      date: piece.publishingAt,
      recordingDate: piece.recording_date,
    },
  });
  const [updatePiece, { loading }] = useMutation(UPDATE_PIECE);
  const { notifySaveSuccess, notifySaveError } = useNotification();

  useEffect(() => {
    setValue('date', piece.publishingAt, {
      shouldDirty: false,
    });
    setValue('recordingDate', piece.recording_date, {
      shouldDirty: false,
    });
    reset();
  }, [piece]);

  const handleSave: SubmitHandler<any> = async (data) => {
    try {
      if (isInterview && dirtyFields.recordingDate) {
        await updatePiece({
          variables: {
            pieceId: piece.id,
            key: 'recording_date',
            value: data.recordingDate,
          },
        });
      }
      if (dirtyFields.date) {
        await updatePiece({
          variables: {
            pieceId: piece.id,
            key: 'publishingAt',
            value: data.date,
          },
        });
      }
      notifySaveSuccess();
      reset();
      refetch();
      refectDates();
    } catch {
      notifySaveError();
    }
  };

  usePrompt({
    isDirty: Object.keys(dirtyFields).length > 0,
  });

  return (
    <div>
      <ActionTypeCom
        index={indexAction}
        totalActions={total}
        platform={piece.platform}
        type={piece.type}
        iconSize="size-[20px]"
      />
      <form
        onSubmit={handleSubmit(handleSave)}
        className="flex flex-row gap-2.5"
      >
        {isInterview && (
          <DatePickerInput
            control={control}
            name="recordingDate"
            id={`actionRecordingDate${piece.id}`}
            placeholder={t('singleCampaign.recordingDate')}
          ></DatePickerInput>
        )}
        <DatePickerInput
          // limitDate={new Date()}
          control={control}
          name="date"
          id={`actionDate${piece.id}`}
          placeholder={dateLabel}
        ></DatePickerInput>
        <Button
          text={t('global.save')}
          style="light-blue"
          type="submit"
          loading={loading}
          customClassnames="!px-14 flex items-center justify-center"
        />
      </form>
    </div>
  );
}
